/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { Controller, FieldValues, Path, useForm } from "react-hook-form";
import { Else, If, Then } from "react-if";
import { usePrevious, useToggle } from "react-use";
import { Button, ExtendedTooltip, FlexContainer, Input, Text, theme } from "@nordcloud/gnui";
import { Description } from "~/components/Styled";
import { isNotNil, isNotPrimitive } from "~/tools";
import { EditableDetailsFormValue, FormField } from "./types";

type Props<T extends EditableDetailsFormValue> = {
  field: FormField<T>;
  model: T;
  onChange: (data: Partial<T>) => Promise<void>;
};

export function Field<T extends FieldValues>({ field, model, onChange }: Props<T>) {
  const [isEditing, toggleIsEditing] = useToggle(false);
  const hasBeenEditable = usePrevious(field.editable);
  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    setFocus,
    formState: { isSubmitting },
  } = useForm<T>({
    shouldUnregister: true,
  });

  const inputName = field.key;
  const value = watch(inputName) ?? model[field.key];
  const displayValue = getDisplayValue(value);
  const isEditable = !isEditing && field.editable;

  async function onSubmit(data: T) {
    await onChange(data as Partial<T>);
    toggleIsEditing();
  }

  function handleCancel() {
    toggleIsEditing();
    reset();
  }

  useEffect(() => {
    if (isEditing) {
      setFocus(inputName, { shouldSelect: true });
    }
  }, [inputName, isEditing, setFocus]);

  useEffect(() => {
    if (hasBeenEditable && !field.editable) {
      toggleIsEditing(false);
    }
  });

  return (
    <FlexContainer
      as="form"
      columnGap={theme.spacing.spacing04}
      css={{ maxWidth: "60rem" }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <If condition={isEditing}>
        <Then>
          <Description css={{ lineHeight: 1 }}>{field.label}</Description>
          <If condition={isNotNil(field.editComponent)}>
            <Then>
              {() => (
                <Controller
                  name={inputName}
                  control={control}
                  render={({ field: { onChange: fieldOnChange } }) => (
                    // @ts-expect-error It seems there is slight bug in TS. It complains about lack of constructor,
                    // because this component can be undefined in type. Completely ignores previous check.
                    <field.editComponent value={value} onChange={fieldOnChange} />
                  )}
                />
              )}
            </Then>
            <Else>
              <Input
                placeholder={field.key}
                defaultValue={value as never}
                {...register(inputName, { required: true })}
              />
            </Else>
          </If>
          <FlexContainer gap={theme.spacing.spacing03}>
            <ExtendedTooltip caption="Save">
              <Button type="submit" severity="low" icon="save" disabled={isSubmitting} data-testid="save-button" />
            </ExtendedTooltip>
            <ExtendedTooltip caption="Cancel">
              <Button
                severity="low"
                icon="close"
                type="button"
                disabled={isSubmitting}
                data-testid="cancel-button"
                onClick={handleCancel}
              />
            </ExtendedTooltip>
          </FlexContainer>
        </Then>
        <Else>
          <FlexContainer
            columnGap={theme.spacing.spacing04}
            css={{
              paddingRight: "4rem",
              "&:hover": {
                "> .edit-btn": {
                  display: isEditable ? "block" : "none",
                },
                ".value-display": {
                  paddingBottom: field.editable ? `calc(${theme.spacing.spacing01} + 1px)` : theme.spacing.spacing01,
                  borderBottom: "none",
                },
              },
            }}
          >
            <div>
              <Description>{field.label}</Description>
              <If condition={isNotNil(field.viewComponent)}>
                <Then>
                  {/* @ts-expect-error It seems there is slight bug in TS. It complains about lack of constructor, see above */}
                  {() => <field.viewComponent value={value} />}
                </Then>
                <Else>
                  <Text
                    mb={0}
                    className="value-display"
                    css={{
                      cursor: "text",
                      width: "100%",
                      paddingBottom: theme.spacing.spacing01,
                      borderBottom: isEditable ? `1px solid ${theme.color.border.border02}` : "none",
                    }}
                    onClick={() => isEditable && toggleIsEditing()}
                  >
                    {displayValue}
                  </Text>
                </Else>
              </If>
            </div>
            <div className="edit-btn" css={{ display: "none" }}>
              <ExtendedTooltip caption="Edit">
                <Button icon="edit" type="button" severity="low" data-testid="edit-button" onClick={toggleIsEditing} />
              </ExtendedTooltip>
            </div>
          </FlexContainer>
        </Else>
      </If>
    </FlexContainer>
  );
}

function getDisplayValue<T extends EditableDetailsFormValue>(value: T[Path<T>]): string {
  if (value == null) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (isNotPrimitive(value)) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string,@typescript-eslint/restrict-template-expressions
    return "toString" in value ? value.toString() : `${value}`;
  }

  return value.toString();
}
